'use client';

import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Link,
  Steps,
  Text,
  useSteps,
} from '@chakra-ui/react';
import { toaster } from '@components/ui/toaster';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';

import BusinessInfoStep from '@features/onboarding/BusinessInfoStep';
import IdentityDocumentsStep from '@features/onboarding/IdentityDocumentsStep';
import {
  BusinessInfoFormState,
  emptyBusinessInfoFormState,
  emptyIdentityDocsFormState,
  emptyPersonalInfoFormState,
  IdentityDocsFormState,
  PersonalInfoFormState,
} from '@features/onboarding/kycTypes';
import PersonalInfoStep from '@features/onboarding/PersonalInfoStep';
import { useOrgStore } from 'stores/rootStore';

const stepConfigs = [
  {
    stepId: 'personalInfo',
    title: 'Personal Information',
    description: 'Basic details',
  },
  {
    stepId: 'identityDocs',
    title: 'Identity Documents',
    description: 'Upload ID documents',
  },
  {
    stepId: 'businessInfo',
    title: 'Business Information',
    description: 'Business details',
  },
];

interface Props {
  disableSkip?: boolean;
}

const KycPage = ({ disableSkip = false }: Props) => {
  const orgStore = useOrgStore();
  const stepState = useSteps({ count: stepConfigs.length });
  const isFinalStep = stepState.value + 1 === stepState.count;

  const [personalInfo, setPersonalInfo] = useState<PersonalInfoFormState>(
    emptyPersonalInfoFormState(),
  );
  const [_identityDocs, setIdentityDocs] = useState<IdentityDocsFormState>(
    emptyIdentityDocsFormState(),
  );
  const [_businessInfo, setBusinessInfo] = useState<BusinessInfoFormState>(
    emptyBusinessInfoFormState(),
  );
  const [personalInfoValid, setPersonalInfoValid] = useState(false);
  const [identityDocsValid, setIdentityDocsValid] = useState(false);
  const [businessInfoValid, setBusinessInfoValid] = useState(false);

  const isCurrentStepValid = useMemo(() => {
    if (stepState.value === 0) {
      return personalInfoValid;
    }
    if (stepState.value === 1) {
      return identityDocsValid;
    }
    if (stepState.value === 2) {
      return businessInfoValid;
    }
    return false;
  }, [stepState.value, personalInfoValid, identityDocsValid, businessInfoValid]);

  const handlePersonalInfoChange = useCallback((state: PersonalInfoFormState, isValid: boolean) => {
    setPersonalInfo(state);
    setPersonalInfoValid(isValid);
  }, []);

  const handleIdentityDocsChange = useCallback((state: IdentityDocsFormState, isValid: boolean) => {
    setIdentityDocs(state);
    setIdentityDocsValid(isValid);
  }, []);

  const handleBusinessInfoChange = useCallback((state: BusinessInfoFormState, isValid: boolean) => {
    setBusinessInfo(state);
    setBusinessInfoValid(isValid);
  }, []);

  const handleNext = useCallback(() => {
    if (isFinalStep) {
      toaster.success({
        title: 'KYC Submitted',
        description: 'Your verification information has been received. We will be in touch.',
      });
      stepState.goToNextStep();
      return;
    }
    stepState.goToNextStep();
  }, [isFinalStep, stepState]);

  const handlePrev = useCallback(() => {
    stepState.goToPrevStep();
  }, [stepState]);

  const stepValidity = useMemo(
    () => [personalInfoValid, identityDocsValid, businessInfoValid],
    [personalInfoValid, identityDocsValid, businessInfoValid],
  );

  const redirectHref = useMemo(() => {
    const selectedOrgSlug = orgStore.selectedOrg?.slug;
    if (selectedOrgSlug != null) {
      return `/${selectedOrgSlug}`;
    }
    return '/';
  }, [orgStore.selectedOrg?.slug]);

  const canNavigateTo = useCallback(
    (index: number): boolean => {
      // Already on completed screen — no navigation
      if (stepState.value >= stepState.count) {
        return false;
      }
      // Same step — no-op
      if (index === stepState.value) {
        return false;
      }
      // Going back is always allowed
      if (index < stepState.value) {
        return true;
      }
      // Going forward: all steps before the target must be valid
      for (let i = 0; i < index; i++) {
        if (!stepValidity[i]) {
          return false;
        }
      }
      return true;
    },
    [stepState.value, stepState.count, stepValidity],
  );

  const handleStepClick = useCallback(
    (index: number) => {
      if (canNavigateTo(index)) {
        stepState.setStep(index);
      }
    },
    [canNavigateTo, stepState],
  );

  const hasPassedStep = useCallback((index: number) => index < stepState.value, [stepState.value]);

  return (
    <Flex direction="column" gap={6} maxW="4xl" mx="auto" w="full">
      <Box>
        <Heading size="lg">Complete KYC Verification</Heading>
        <Text color="text.secondary" fontSize="sm" fontWeight="medium" mt={1}>
          We need to verify your identity and business details to comply with regulations.
        </Text>
      </Box>
      <Steps.RootProvider value={stepState} orientation="vertical" size="sm">
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          gap={{ base: 6, lg: 10 }}
          align="flex-start"
        >
          <Box w={{ base: 'full', lg: '240px' }} flexShrink={0} pt={1}>
            <Steps.List flexDirection={{ base: 'row', lg: 'column' }}>
              {stepConfigs.map((step, index) => (
                <Steps.Item
                  key={step.stepId}
                  index={index}
                  title={step.title}
                  flex={{ base: 1, lg: 'unset' }}
                  minH={{ lg: '72px' }}
                  cursor={canNavigateTo(index) ? 'pointer' : 'default'}
                  onClick={() => handleStepClick(index)}
                >
                  <Steps.Indicator borderColor="border.light" />
                  <Box>
                    <Steps.Title
                      fontSize={{ base: 'xs', lg: 'sm' }}
                      fontWeight={hasPassedStep(index) ? 'normal' : 'medium'}
                    >
                      {step.title}
                    </Steps.Title>
                    <Steps.Description fontSize="xs" color="text.secondary" fontWeight="medium">
                      {step.description}
                    </Steps.Description>
                  </Box>
                  {index < stepConfigs.length - 1 && (
                    <Steps.Separator
                      borderColor="border.light"
                      display={{ base: 'none', lg: 'block' }}
                    />
                  )}
                </Steps.Item>
              ))}
            </Steps.List>
          </Box>

          <Flex flex={1} direction="column" gap={6} w="full">
            <Steps.Content
              index={0}
              borderColor="border.light"
              borderWidth={1}
              borderRadius="md"
              p={{ base: 4, md: 6 }}
            >
              <PersonalInfoStep onFormStateChange={handlePersonalInfoChange} />
            </Steps.Content>

            <Steps.Content
              index={1}
              borderColor="border.light"
              borderWidth={1}
              borderRadius="md"
              p={{ base: 4, md: 6 }}
            >
              <IdentityDocumentsStep
                country={personalInfo.country}
                onFormStateChange={handleIdentityDocsChange}
              />
            </Steps.Content>

            <Steps.Content
              index={2}
              borderColor="border.light"
              borderWidth={1}
              borderRadius="md"
              p={{ base: 4, md: 6 }}
            >
              <BusinessInfoStep
                country={personalInfo.country}
                onFormStateChange={handleBusinessInfoChange}
              />
            </Steps.Content>

            <Steps.CompletedContent>
              <Flex
                direction="column"
                align="center"
                gap={3}
                p={8}
                borderColor="border.light"
                borderWidth={1}
                borderRadius="md"
                textAlign="center"
              >
                <Heading size="md">Verification Submitted</Heading>
                <Text color="text.secondary" fontSize="sm" fontWeight="medium" maxW="sm">
                  Your KYC information has been submitted for review. We will notify you once
                  verification is complete.
                </Text>
              </Flex>
            </Steps.CompletedContent>

            {stepState.value < stepState.count && (
              <ButtonGroup size="sm">
                {stepState.value > 0 && (
                  <Button
                    variant="outline"
                    borderColor="border.light"
                    _hover={{ backgroundColor: 'button.secondary.hover' }}
                    onClick={handlePrev}
                  >
                    Back
                  </Button>
                )}
                <Button variant="solid" disabled={!isCurrentStepValid} onClick={handleNext}>
                  {isFinalStep ? 'Complete KYC' : 'Continue'}
                </Button>
              </ButtonGroup>
            )}
          </Flex>
        </Flex>
      </Steps.RootProvider>
      {!disableSkip && (
        <Flex justify="center" mt={4}>
          <Text>
            I want to{' '}
            <Link href={redirectHref} variant="underline" fontWeight="semibold">
              explore more
            </Link>
            . I'll complete this process later
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

export default observer(KycPage);
